import { faHeart } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Props as ButtonProps } from 'atoms/buttons/Button';
import { Button } from 'atoms/buttons/Button';
import { FontAwesomeIcon } from 'atoms/icons';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useSubscribe } from 'hooks/subscriptions/useSubscribe';
import { useUnsubscribe } from 'hooks/subscriptions/useUnsubscribe';
import { useLoggedCallback } from 'hooks/useLoggedCallback';
import { useSendUserEngagementIncrement } from 'hooks/useSendUserEngagementIncrement';
import { glossary } from 'lib/glossary';
import { UserEngagementScoreIncrement_Source } from 'protos/webview/channel/native_messages';

type Subscription = {
  slug: string;
};

type Subscribable = {
  __typename: string;
  slug: string;
};

interface Props extends ButtonProps {
  subscribable: Subscribable;
  initialSubscription?: Subscription | null;
  withIcon?: boolean;
  callback?: () => void;
  renderButton?: (
    props: Omit<ButtonProps, 'size'>,
    subscription: Subscription | null | undefined,
    hovered: boolean
  ) => React.JSX.Element;
}

export const FollowButton = ({
  subscribable,
  initialSubscription,
  withIcon,
  renderButton,
  callback,
  ...rest
}: Props) => {
  const [subscription, setSubscription] = useState<
    Subscription | null | undefined
  >(initialSubscription);

  if (initialSubscription && typeof subscription === 'undefined') {
    setSubscription(initialSubscription);
  }

  const [hover, setHover] = useState<boolean>(false);
  const unsubscribe = useUnsubscribe();
  const subscribe = useSubscribe();
  const { currentUser } = useCurrentUserContext();
  const { sendUserEngagementIncrement } = useSendUserEngagementIncrement();

  const loggedHandler = useLoggedCallback<
    React.MouseEvent<HTMLElement, MouseEvent>
  >(event => {
    event.stopPropagation();

    if (subscription) {
      unsubscribe(subscription).then(() => {
        setSubscription(null);
        callback?.();
      });
    } else {
      subscribe(subscribable).then(({ data }) => {
        if (!data) return;
        setSubscription({
          slug: data.createSubscription!.subscription!.slug,
        });
        callback?.();

        sendUserEngagementIncrement(
          25,
          UserEngagementScoreIncrement_Source.START_FOLLOWING_USER
        );
      });
    }
  });

  if (
    currentUser?.slug === subscribable.slug &&
    subscribable.__typename === 'User'
  ) {
    return null;
  }

  const color = subscription && hover ? 'red' : 'tertiary';
  const messageIfHover = hover ? (
    <FormattedMessage {...glossary.unfollow} />
  ) : (
    <FormattedMessage {...glossary.followed} />
  );

  const label = subscription ? (
    messageIfHover
  ) : (
    <FormattedMessage {...glossary.follow} />
  );

  const buttonProps = {
    onClick: loggedHandler,
    onMouseEnter: () => {
      if (subscription) setHover(true);
    },
    onMouseLeave: () => {
      if (subscription) setHover(false);
    },
    children: label,
    ...rest,
  };

  if (renderButton) {
    return renderButton(buttonProps, subscription, hover);
  }

  return (
    <Button color={color} {...buttonProps}>
      {!!withIcon && <FontAwesomeIcon icon={faHeart} />}
      <span>
        {subscription ? (
          messageIfHover
        ) : (
          <FormattedMessage
            id="FollowButton.text"
            defaultMessage="Add to My Circle"
          />
        )}
      </span>
    </Button>
  );
};
